import { useResource } from "hooks/useResource";
import { getSlides } from "services/content";
import { useSlideshow, layerClass, resolveSlideText, slideShowsText, shadeCss } from "lib/slider";
import SlideImage from "components/shared/SlideImage";
import JourneyStrip from "components/home/JourneyStrip";
import NoticeMarquee from "components/home/NoticeMarquee";

const DEFAULT_DURATION = 5.5;

export default function HeroSlider() {
  const { data: slides, loading } = useResource(getSlides);
  const show = useSlideshow(slides, { defaultDuration: DEFAULT_DURATION });

  if (loading) {
    return (
      <section
        className="relative h-[580px] overflow-hidden animate-pulse"
        style={{ background: "linear-gradient(140deg,#003d2b,#005C40)" }}
        aria-busy="true"
      />
    );
  }
  // No slides published (admin → Homepage) — skip the hero entirely rather
  // than pinning an empty 580px block above the journey.
  if (!slides?.length) return null;

  const SLIDES = slides;
  const { cur } = show;
  const s = SLIDES[cur];
  // Caption: per-slide "show text" switch, and "same text as previous slide"
  // (the photo changes, the caption stays and doesn't re-animate).
  const { text, key: textKey } = resolveSlideText(SLIDES, cur);
  const hasText = slideShowsText(s) && (text.headline || text.sub || text.badge);

  return (
    <section
      /* One screen on every size: the section fills the viewport below the
         header, the journey strip takes the height it needs, and the photo
         area (flex-1) absorbs whatever is left. That keeps the next section
         off the first screen instead of leaving a pale gap under the stats. */
      className="relative overflow-hidden flex flex-col min-h-[calc(100svh-100px)] lg:min-h-[clamp(520px,calc(100vh-100px),860px)] bg-green-dark"
      aria-roledescription="carousel"
      aria-label="Highlights"
    >
      {/* Photo area. The school asked that the institution cards NOT sit on top
          of the photo, so the image stops here and the strip follows below it
          rather than overlaying — everything photo-related is clipped to this
          box. */}
      <div className="relative flex-1 flex flex-col overflow-hidden">
      {/* One stacked layer per slide; lib/slider.js + the .sl-* CSS run the
          transition the slide asks for (admin → Homepage → slide options). */}
      {SLIDES.map((sl, i) => {
        const hasPhoto = !!(sl.image || sl.mobileImage);
        return (
          <div
            key={i}
            aria-hidden={i !== cur}
            className={layerClass(i, show, sl.animation)}
            style={{ background: sl.bg, "--sl-dir": show.dir }}
          >
            {hasPhoto ? (
              <>
                {/* Desktop photo, swapped for the mobile upload under 768px. */}
                <SlideImage slide={sl} />
                {/* Optional dark wash — off by default so photos stay clean;
                    set per slide in admin when text needs the contrast. */}
                {shadeCss(sl.shade) && (
                  <div className="absolute inset-0" style={{ background: shadeCss(sl.shade) }} />
                )}
              </>
            ) : (
              <svg className="absolute inset-0 w-full h-full opacity-[0.06]" viewBox="0 0 1200 580">
                {Array.from({ length: 8 }).map((_, r) =>
                  Array.from({ length: 15 }).map((_, c) => (
                    <circle
                      key={`${r}-${c}`}
                      cx={c * 90 + 45}
                      cy={r * 80 + 40}
                      r="26"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="1"
                    />
                  ))
                )}
              </svg>
            )}
          </div>
        );
      })}

      {/* Text fills whatever height is left above the strip and centres in it */}
      <div className="relative z-[2] w-full max-w-container mx-auto px-6 sm:px-10 pt-10 pb-8 lg:pb-4 flex-1 flex items-center">
        {hasText && (
          <div key={textKey} className="sl-cap flex-1">
            {text.badge && (
              <div
                className="inline-block bg-white/[0.12] border border-white/[0.22] rounded-full px-[18px] py-[5px] text-[11px] tracking-[0.14em] uppercase font-bold mb-5"
                style={{ color: text.accent }}
              >
                {text.badge}
              </div>
            )}
            <h1
              className="font-serif font-extrabold text-white leading-[1.15] mb-[18px] whitespace-pre-line"
              style={{
                fontSize: "clamp(34px,4.5vw,60px)",
                textShadow: "0 2px 24px rgba(0,0,0,0.25)",
              }}
            >
              {text.headline}
            </h1>
            {text.sub && (
              <p className="text-white/80 text-base leading-[1.7] max-w-[520px]">
                {text.sub}
              </p>
            )}
            {/* No buttons on the slider — the school asked for none; the
                journey strip below is the call to action. */}
          </div>
        )}
      </div>

      {/* Dots — top right so they never collide with the strip */}
      <div className="absolute top-6 right-6 sm:right-10 flex gap-2 z-10">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => show.go(i)}
            aria-label={`Go to slide ${i + 1}`}
            aria-current={i === cur ? "true" : undefined}
            className="h-[9px] rounded-[5px] border-none cursor-pointer transition-all duration-300 p-0"
            style={{
              width: i === cur ? 30 : 9,
              background: i === cur ? "#fff" : "rgba(255,255,255,0.35)",
            }}
          />
        ))}
      </div>

      {/* Arrows */}
      {[
        { onClick: show.back, side: "left", a: "‹" },
        { onClick: show.next, side: "right", a: "›" },
      ].map(({ onClick, side, a }) => (
        <button
          key={side}
          onClick={onClick}
          aria-label={side === "left" ? "Previous slide" : "Next slide"}
          className="absolute top-[30%] bg-white/[0.11] border border-white/[0.22] text-white w-11 h-11 rounded-full text-[26px] cursor-pointer z-10 backdrop-blur-[4px] hidden sm:flex items-center justify-center leading-none transition-colors hover:bg-white/[0.24]"
          style={{ [side]: 22 }}
        >
          {a}
        </button>
      ))}
      </div>
      {/* End of the photo area. */}

      {/* Running notices, between the photo and the institution cards. */}
      <NoticeMarquee />

      {/* Journey strip — sits BELOW the photo on its own solid background, so
          the institution names never overlap the image (school request). */}
      <div className="hidden lg:block relative z-[3] w-full pt-4 pb-5">
        <JourneyStrip />
      </div>
      {/* Same position on phones and tablets, just the stacked styling. */}
      <div className="lg:hidden relative z-[3] w-full">
        <JourneyStrip overlay={false} />
      </div>
    </section>
  );
}
